import { defineStore } from 'pinia';
import { apolloClient } from '@/utils/apollo';
import { useDepartmentStore } from '@/stores/department';
import { format } from '@/utils/date-fns';
import { get } from 'lodash';
import { bookingResourcesQuery } from '@/graphql/queries/bookingResources.js';
import { bookingSlotsQuery } from '@/graphql/queries/bookingSlots.js';
import { bookResourceScreenMutation } from '@/graphql/mutations/bookResourceScreen.js';
import { bookSlottedResourceScreenMutation } from '@/graphql/mutations/bookSlottedResourceScreen.js';
import { cancelSlottedBookingScreenMutation } from '@/graphql/mutations/cancelSlottedBookingScreen.js';
import { getAllResourceBookings } from '@/utils/bookings';
import { endOfWeek, startOfWeek } from 'date-fns';
import { useProfilesStore } from '@/stores/profiles';
import { IDepartment, IImage, IInstitution, IProfile } from '@/interfaces';

export interface IBookingResource {
  id: string;
  slotted: boolean;
  institution: IInstitution;
  title: string;
  description: string;
  authentication: string; // TODO: Add possible stringss
  coverImage: IImage;
  bookings?: IStandardBooking[];
  bookingSlots?: ISlottedBooking[];
  departments: IDepartment[];
}

export interface IStandardBooking {
  id: string;
  selectedStartDate: string;
  selectedStartTime: string | null;
  selectedEndDate: string;
  selectedEndTime: string | null;
  title: string;
  description: string;
  resourceId: string | null;
  participants: IProfile[];
  start: string | undefined;
  end: string | undefined;
}

export interface ISlottedBooking {
  id: string;
  participants: IProfile[];
  bookingSlot: IBookingSlot;
}

export interface IBookingSlot {
  bookingResourceId: string | null;
  bookingResourceSlotId: string | null;
  date: string;
}

export interface ISlottedWeek {
  dateFrom: string | Date | null;
  dateTo: string | Date | null;
}

export interface IBookingResourcesState {
  all: IBookingResource[];
  newBooking: IStandardBooking;
  newSlottedBooking: ISlottedBooking;
  resourceBookings: any;
  selectedSlottedWeek: ISlottedWeek;
  participating: boolean;
  selectedBookingSlots: IBookingSlot[];
  selectedBookingEntry?: IStandardBooking | null;
}

export const useBookingResourcesStore = defineStore('bookingResources', {
  state: (): IBookingResourcesState => ({
    all: [],
    resourceBookings: null,
    selectedSlottedWeek: {
      dateFrom: null,
      dateTo: null,
    },
    participating: false,
    newBooking: {
      id: '',
      participants: [],
      selectedStartDate: '',
      selectedStartTime: null,
      selectedEndDate: '',
      selectedEndTime: null,
      title: '',
      resourceId: null,
      description: '',
      start: '',
      end: '',
    },
    selectedBookingEntry: null,
    selectedBookingSlots: [],
    newSlottedBooking: {
      id: '',
      participants: [],
      bookingSlot: {
        bookingResourceId: null,
        bookingResourceSlotId: null,
        date: '',
      },
    },
  }),
  actions: {
    async getBookingResources() {
      const departmentStore = useDepartmentStore();

      if (!departmentStore.id) {
        throw new Error('No department id provided');
      }

      await apolloClient
        .query({
          query: bookingResourcesQuery,
          variables: {
            filter: {
              departments: departmentStore.id,
            },
            timezone: 'Europe/Copenhagen',
          },
          fetchPolicy: 'no-cache',
        })
        .then((response) => {
          const resources = response.data.bookingResources;

          this.all = resources;
          this.resourceBookings = getAllResourceBookings(resources);
        })
        .catch((error) => {
          console.log('Error getting bookings', error);
        });
    },
    async getBookingSlots({
      id,
      dateFrom,
      dateTo,
    }: {
      id: string;
      dateFrom: string;
      dateTo: string;
    }) {
      await apolloClient
        .query({
          query: bookingSlotsQuery,
          variables: {
            bookingResourceId: id,
            dateFrom,
            dateTo,
            timezone: 'Europe/Copenhagen',
          },
          fetchPolicy: 'no-cache',
        })
        .then((response) => {
          this.selectedBookingSlots = response.data.bookingSlots;
        })
        .catch((error) => {
          console.log(`Error getting bookingSlots from id ${id}`, error);
        });
    },
    setSelectedBookingEntry(bookingEntry?: IStandardBooking) {
      this.selectedBookingEntry = bookingEntry;
    },
    setSelectedSlottedWeek(week: ISlottedWeek) {
      if (!week) {
        const currentWeek = {
          dateFrom: startOfWeek(new Date(), { weekStartsOn: 1 }),
          dateTo: endOfWeek(new Date(), { weekStartsOn: 1 }),
        };

        this.selectedSlottedWeek = currentWeek;
        return;
      }
      this.selectedSlottedWeek = week;
    },
    setNewBooking(booking: any) {
      this.$patch({
        newBooking: {
          ...this.newBooking,
          ...booking,
        },
      });
    },
    setNewSlottedBooking(booking: ISlottedBooking) {
      this.newSlottedBooking = booking;
    },
    async createNewBooking() {
      const participants = this.newBooking.participants.map((participant) => {
        return {
          id: participant.id,
          type: participant.type,
        };
      });

      const now = new Date();
      const timezone = format(now, 'XXX');
      const end = `${this.newBooking.selectedEndDate}T${this.newBooking.selectedEndTime}:00${timezone}`;
      const start = `${this.newBooking.selectedStartDate}T${this.newBooking.selectedStartTime}:00${timezone}`;

      const booking = {
        bookingResourceId: this.newBooking.resourceId,
        end,
        start,
        title: this.newBooking.title,
      };

      let newBookingId;

      await apolloClient
        .mutate({
          mutation: bookResourceScreenMutation,
          variables: {
            participants,
            booking,
          },
        })
        .then(async (response) => {
          newBookingId = get(
            response,
            'data.booking.bookResourceScreen.id',
            undefined
          );

          if (!newBookingId)
            throw new Error('Error creating booking. No bookingId provided');
        })
        .catch((error) => {
          throw new Error('Error saving new booking', error);
        });

      return newBookingId;
    },
    async setParticipating(participating: boolean) {
      this.participating = participating;
    },
    async createNewSlottedBooking(
      id: string,
      type: string,
      slot: IBookingSlot
    ) {
      const profilesStore = useProfilesStore();

      await apolloClient
        .mutate({
          mutation: bookSlottedResourceScreenMutation,
          variables: {
            participants: [
              {
                id,
                type,
              },
            ],
            bookingSlot: slot,
          },
        })
        .then(() => {
          profilesStore.setPinValidation({});
        })
        .catch((error) => {
          throw new Error(`Error creating slotted booking', ${error}`);
        });
    },
    async cancelSlottedBooking(profile: IProfile, slot: IBookingSlot) {
      const profilesStore = useProfilesStore();

      await apolloClient
        .mutate({
          mutation: cancelSlottedBookingScreenMutation,
          variables: {
            profile,
            bookingSlot: slot,
          },
        })
        .then(() => {
          profilesStore.setPinValidation({});
        })
        .catch((error) => {
          profilesStore.setPinValidation({
            validating: false,
            validPin: false,
          });

          throw new Error('Error cancelling booking', error);
        });
    },
  },
});
